import type { FC } from 'react';
import { useId } from 'react';
import { MdOutlineConfirmationNumber } from 'react-icons/md';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { getCurrentLocale } from 'src/helper/helper';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import HorizontalBarChart from 'src/components/followUp/common/BaseWidgets/HorizontalBarChart/HorizontalBarChart';
import type { AggregateList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { useExportOptions } from './hooks';

const HorizontalBarChartWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const locale = getCurrentLocale();
  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.AGGREGATE_LIST,
    config.id,
  );

  const { data, errorMsg, refetch, isFetching, queryFilters } =
    useWidgetQuery<AggregateList>(endpoint);

  const exportOptions = useExportOptions({
    data: data ?? null,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      error={errorMsg}
      loading={isFetching}
      maxHeight="auto"
      exportOptions={exportOptions}
      header={
        <WidgetHeaderWithPill
          title={config.name}
          pillProps={{
            label: (data?.totalObjects ?? 0).toLocaleString(locale),
            icon: <MdOutlineConfirmationNumber />,
            color: 'primary',
          }}
          ariaId={ariaId}
        />
      }
      footer={
        data?.totalObjects === 0 ? (
          <NoResultsToShowAdjustFiltersWarning />
        ) : undefined
      }
    >
      <HorizontalBarChart
        bars={data?.results ?? []}
        totalObjects={data?.totalObjects ?? 0}
      />
    </WidgetFrame>
  );
};

export default HorizontalBarChartWidget;
